import AwardsSection from "@components/new-ui/AwardsSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import { container_classname } from "@components/new-ui/constants";
import CTASection from "@components/new-ui/CTASection";
import IntegrationSection from "@components/new-ui/IntegrationSection";
import NewLayout from "@components/new-ui/Layout";
import { isJSXElement } from "@utils/common";
import React, { ReactNode } from "react";
import { IoIosCheckmarkCircle } from "react-icons/io";

export interface PressReleaseHerosection {
    title: string;
    description: string;
    releaseDate: string;
}

interface ListItem {
    title?: string;
    description?: {
        item: string;
    }[];
    customeStyle?: string;
    icon?: ReactNode;
}
export interface PressReleaseSummary {
    title?: string | JSX.Element;
    description: JSX.Element;
    list?: ListItem[];
}

export interface PressRelease {
    summary?: PressReleaseSummary[];
    aboutData?: PressReleaseSummary[];
}

const content: PressReleaseSummary[] = [
    {
        title: "Notes to the editor",
        description: (
            <p>
                <a href="https://drive.google.com/drive/u/0/folders/1jOF3WSTa4_t5Us22NW-1tAsx2-poOhjX" className="text-blue-500">
                    Media images can be found here
                </a>
                . For further information please contact the Gallabox press office: Bilal Mahmood on b.mahmood@stockwoodstrategy.com or +44 (0) 771 400 7257.
            </p>
        ),
    },
    {
        title: "About Gallabox",
        description: (
            <p>
                Gallabox is a no-code conversational platform on a mission to democratize AI for small and medium-sized businesses worldwide. With offices in California, Dubai, and
                India, Gallabox is transforming the sales and marketing playbooks of thousands of companies, helping them achieve faster growth than ever before.
            </p>
        ),
    },
    {
        title: "About FUSE",
        description: <p>FUSE is an early-stage software focused venture capital firm, currently investing out of their second fund of $255M.</p>,
    },
    {
        title: "About Prime Venture Partners",
        description: (
            <p>
                Prime Venture Partners is one of India’s leading early-stage venture capital firms (primevp.in) based in Bangalore, led by Sanjay Swamy, Shripati Acharya, and Amit
                Somani. Founded in 2012 with the goal of bringing Silicon Valley-style professionalism to venture investing and building world-class companies from India, PrimeVP
                is often the first institutional investor in category-defining tech startups. Its major investments span sectors including FinTech, Enterprise SaaS, Consumer
                Internet, EdTech, Healthcare, Logistics, IoT, and EVs, among others.
            </p>
        ),
    },
];

const mainContentData: PressReleaseSummary[] = [
    {
        description: (
            <p>
                As traditional business communication channels see declining engagement, Gallabox enables SMBs to deploy enterprise-grade AI automation on WhatsApp, serving over
                10,000 businesses across 45 countries
            </p>
        ),
    },
    {
        description: (
            <p>
                San Diego, California – January 29, 2025; Traditional customer communication channels are breaking down, with businesses losing millions in potential revenue
                through ineffective email, SMS and phone outreach. Gallabox, founded by veterans from India&apos;s leading services marketplace, announced $3.5 million in funding
                to transform how businesses leverage WhatsApp for marketing and sales.
            </p>
        ),
    },

    {
        description: (
            <p>
                The seed round was led by FUSE, with participation from existing investors Prime Venture Partners and Neon Fund. This latest round brings the total raised by
                Gallabox to $5 million since its inception in 2021.
            </p>
        ),
    },

    {
        description: (
            <p>
                Businesses worldwide, especially in regions where WhatsApp dominates communication, are eager to integrate this high-ROI channel into their growth strategies. While
                large enterprises could afford to build custom WhatsApp automation solutions to nurture conversations and convert them to closed deals, small and mid-sized
                businesses were left managing conversations manually, leading to significant revenue leakages and lost opportunities.
            </p>
        ),
    },

    {
        description: (
            <p>
                Unlike traditional communication tools that offer fragmented solutions, Gallabox provides an end-to-end platform for WhatsApp automation. The platform&apos;s
                no-code approach enables businesses to create AI chatbots for lead qualification, deploy drip marketing campaigns, and manage team collaboration through shared
                inboxes. From broadcast messaging and automated appointment booking to personalized service recommendations, Gallabox helps businesses automate every stage of their
                customer interaction funnel.
            </p>
        ),
    },

    {
        description: (
            <p>
                Founded by Karthik Jagannathan, Yogesh Narayanan, and Yathin Panchanathan, Gallabox emerged from their firsthand experience of working with thousands of SMBs
                struggling with customer communication and scaling growth—despite investing heavily in lead-generating resources, which have largely been manual.
            </p>
        ),
    },

    {
        description: (
            <p>
                &quot;The shift to B2C messaging is undeniable, but most businesses lack the tools to capitalize on it,&quot; said Karthik Jagannathan, CEO and co-founder of
                Gallabox. &quot;Typically, SMBs spend enormous amounts of time and effort in building pipelines, which are often disjointed and not scalable. Our platform enables
                any business, regardless of their technical expertise or budget, to deploy sophisticated AI agents on WhatsApp, helping them convert every conversation into a
                business opportunity... within minutes.&quot;
            </p>
        ),
    },

    {
        description: (
            <p>
                The company&apos;s trajectory validates its approach. Starting in Chennai, India&apos;s unofficial SaaS capital, Gallabox acquired its first 100 customers outside
                their network within 100 days of launch. So far, the platform has served over 10,000 businesses across 45 countries, with strong traction in service-oriented
                sectors including education, healthcare/wellness, real estate, and travel.
            </p>
        ),
    },

    {
        description: (
            <p>
                “The world is standardizing on WhatsApp as the modern communication and engagement platform. Gallabox enables companies to meet their customers exactly where they
                are — with a holistic marketing, sales, and commerce suite on top of WhatsApp. Thrilled to partner with Karthik, Yogesh, Yathindhar, and the entire Gallabox team as
                they shape the future of business-to-customer communication,” said Kellan Carter, Founding Partner at FUSE.
            </p>
        ),
    },

    {
        description: (
            <p>
                The company has established dedicated teams in the Middle East, Latin America and the US to support its rapid international expansion in markets where WhatsApp is
                the dominant channel for business communication.
            </p>
        ),
    },

    {
        description: (
            <p>
                “Karthik, Yogesh, and Yathin have proven to be an exceptional team throughout the two years we have worked together. Their relentless focus on execution and great
                product made it an easy decision for us to double down on our commitment,” said Sanjay Swamy, Managing Partner, Prime Venture Partners. “Gallabox&apos;s outstanding
                product allows SMBs to effortlessly create and deploy AI agents and workflows that automate customer engagement, enabling business owners to concentrate on their
                core operations.”
            </p>
        ),
    },

    {
        description: (
            <p>
                The platform&apos;s impact is evident across sectors. Educational institutions use Gallabox to automate their entire admission process, healthcare providers
                streamline appointment management, and real estate agencies automate property inquiries and viewing schedules. The platform&apos;s AI agents handle routine
                interactions while enabling human teams to focus on high-value conversations.
            </p>
        ),
    },

    {
        description: (
            <p>
                &quot;Gallabox has transformed the way we communicate with our prospects and customers. From capturing leads to using tailored solutions to engage with users, to
                qualifying leads using advanced chatbot capabilities, to even managing travel bookings on WhatsApp—Gallabox&apos;s platform has helped us boost our business growth
                and team productivity tremendously,&quot; said Hari Ganapthy, Co-founder of Pickyourtrail, one of the fastest-growing DIY holiday booking platforms.
            </p>
        ),
    },

    {
        description: (
            <p>
                Looking ahead, Gallabox is evolving beyond pure WhatsApp automation to become a comprehensive AI Sales and Marketing agent platform for SMBs on multiple messaging
                channels. The company is developing advanced capabilities that will enable businesses to train AI agents through natural language instructions, handling complex
                tasks from lead qualification to prospect engagement to service delivery.
            </p>
        ),
    },

    {
        description: (
            <p>
                Gallabox plans to use the funding to strengthen its product capabilities and expand its geographic footprint. The company&apos;s vision is to redefine how
                businesses engage with customers, making enterprise-grade AI automation accessible to companies of all sizes.
            </p>
        ),
    },
];

const HerosectionData: PressReleaseHerosection = {
    title: "PRESS RELEASE",
    description: "Gallabox raises $3.5M to fuel its mission to democratize AI-driven conversational commerce for SMBs",
    releaseDate: "Wednesday January 29, 2025 at 7am ET",
};
const Press_release_fuel: React.FC = () => {
    return (
        <NewLayout
            title={"Gallabox Press Release | $3.5M Seed Funding"}
            description={
                "Gallabox raises $3.5M in a seed investment led by FUSE to deliver enterprise-grade AI for SMBs. Learn how this funding is going to help businesses with conversational commerce."
            }
            canonical="https://gallabox.com/news/gallabox-raises-3.5-million-to-fuel-its-mission"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/press/gallabox-press-2025.png" }}
        >
            <PressReleaseHerosection {...HerosectionData} />
            <section>
                <MainContent summary={mainContentData} />
            </section>
            <section>
                <MainContent aboutData={content} />
            </section>

            <div>
                <div className="w-full mb-20">
                    <h3 className="text-base text-center font-normal py-2">Trusted by 10,000+ businesses</h3>
                    <ClientLogos />
                </div>
                <IntegrationSection />
                <AwardsSection />
                <CTASection title="Turn connections into conversions" description="Take Gallabox for a 7-day ride" />
            </div>
        </NewLayout>
    );
};

export default Press_release_fuel;

export const PressReleaseHerosection: React.FC<PressReleaseHerosection> = ({ title, description, releaseDate }) => {
    return (
        <section className="md:pt-44 pb-20 text-white mb-24 pt-12" style={{ background: "linear-gradient(265deg, #A1D8FF -7.73%, #396AFF 19.04%, #00238D 61.31%, #000 102.16%)" }}>
            <div className={`${container_classname} flex flex-col gap-3`}>
                <p style={{ color: "#01E6FF;" }}>{title}</p>
                <h5 className="text-gray-50 lg:text-new-h1 text-5xl font-bold gap-2 md:mb-5 mb-2" style={{ fontFamily: "Degular Variable" }}>
                    {description}
                </h5>
                <p>{releaseDate}</p>
            </div>
        </section>
    );
};

export const MainContent: React.FC<PressRelease> = ({ summary, aboutData }) => {
    return (
        <section className={`${container_classname} flex flex-col gap-8 md:px-24`}>
            {summary && summary.map((item, index) => <React.Fragment key={index}>{item.description}</React.Fragment>)}

            {aboutData &&
                aboutData.map((data, index) => (
                    <div key={index}>
                        {isJSXElement(data.title) ? data.title : <p className="text-xl font-semibold">{data?.title}</p>}
                        {data.description}

                        {data.list && (
                            <div className="grid grid-cols-2 gap-5 mt-3">
                                {data.list.map((card, index) => (
                                    <div key={index} className={`border-solid border-l-2 py-5 ${card.customeStyle ? card.customeStyle : "bg-red-50 border-red-500"}`}>
                                        <p className="font-semibold px-6 text-lg">{card.title}</p>
                                        <div className="flex flex-col gap-3 px-5">
                                            {card.description?.map((value, idx) => (
                                                <div key={idx} className="flex items-center gap-3 text-gray-900 text-base">
                                                    <span className="min-w-[1.5rem] shrink-0">{card.icon ? card.icon : <IoIosCheckmarkCircle />}</span>
                                                    <span className="flex-1">{value.item}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
        </section>
    );
};
